import React from 'react';
import ReactECharts from 'echarts-for-react';
import {
  getInternalSEColor,
  buildChartTheme,
  truncateChartLabel,
} from './chartTheme';
import useLaptopCompact from './useLaptopCompact';
import ChartEmptyState from './ChartEmptyState';

const StackedHorizontalBarChart = ({
  data,
  renderZeroWhenEmpty = false,
  zeroSeriesNames = ['Runs on PET Tenants', 'Runs on Shared Tenants', 'Runs on Unknown Tenants'],
  emptyStateTitle = 'No activity for this period',
  emptyStateSubtitle = 'Try changing the time filter.',
  emptyStateCompact = false,
}) => {
  const isLaptopCompact = useLaptopCompact();
  const theme = buildChartTheme(isLaptopCompact);

  const hasData = !!(data?.names?.length && data?.series?.length);
  if (!hasData && !renderZeroWhenEmpty) {
    return (
      <ChartEmptyState
        title={emptyStateTitle}
        subtitle={emptyStateSubtitle}
        compact={emptyStateCompact}
      />
    );
  }

  const normalizedData = hasData
    ? data
    : {
        names: ['SE 1', 'SE 2', 'SE 3', 'SE 4'],
        series: zeroSeriesNames.map((name) => ({ name, data: [0, 0, 0, 0] })),
      };
  const isAllZero = normalizedData.series.every((item) =>
    (item?.data || []).every((value) => Number(value) === 0)
  );

  const yLabels = [...normalizedData.names].reverse();
  const series = normalizedData.series.map((s, idx) => ({
    name: s.name,
    type: 'bar',
    stack: 'total',
    barMaxWidth: 22,
    data: [...s.data].reverse(),
    minPointSize: isAllZero ? 2 : 0,
    itemStyle: {
      color: getInternalSEColor(idx),
    },
    label: { show: false },
  }));

  const options = {
    animation: false,
    grid: {
      ...theme.gridHorizontalBar,
      left: isLaptopCompact ? 10 : 12,
      right: isLaptopCompact ? 34 : 36,
      top: 16,
      bottom: isLaptopCompact ? 62 : 72,
      containLabel: true,
    },
    tooltip: {
      ...theme.tooltipBase,
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
    },
    legend: {
      ...theme.legendBase,
      type: 'scroll',
      bottom: 4,
      left: 'center',
      itemGap: isLaptopCompact ? 12 : 14,
      padding: [10, 0, 0, 0],
      formatter: (name) => truncateChartLabel(name, 18),
    },
    xAxis: {
      type: 'value',
      min: 0,
      max: isAllZero ? 1 : undefined,
      axisLabel: theme.axisLabel,
      splitLine: { show: false },
    },
    yAxis: {
      type: 'category',
      data: yLabels,
      axisLabel: {
        ...theme.axisLabel,
        width: isLaptopCompact ? 150 : 160,
        margin: 10,
        overflow: 'truncate',
        formatter: (value) => truncateChartLabel(value, 18),
      },
    },
    series,
  };

  const rowHeight = isLaptopCompact ? 33 : 36;
  const baseHeight = isLaptopCompact ? 72 : 80;

  return (
    <ReactECharts
      option={options}
      style={{ height: `${Math.max(330, normalizedData.names.length * rowHeight + baseHeight)}px`, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
};

export default StackedHorizontalBarChart;
